import { verifyPhoneRequestSchema, type VerifyPhoneRequest } from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { ResendCodeButton } from '@/components/resend-code-button';
import { useApiClient } from '@/hooks/use-api-client';
import {
  centralLoginPath,
  VERIFY_EMAIL_PARAM,
  VERIFY_PHONE_PARAM,
  verifyEmailPath,
} from '@/lib/auth-paths';

/** Self-serve signup only ever creates a console tenant, so its sign-in is the admin client. */
const ADMIN_CLIENT = 'admin' as const;

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/** Normalised in front of the schema, so the SAME value is validated and sent. */
const normalizeValues = (values: VerifyPhoneRequest): VerifyPhoneRequest => ({
  email: values.email.trim(),
  phone: values.phone.trim(),
  code: values.code.trim(),
});

/**
 * Step three of self-serve signup: redeem the texted code (S26 Phase 3).
 *
 * Public, like `/signup` and `/verify-email`, and for the identical reason — the
 * account is still `PENDING` and cannot be signed in to.
 *
 * The number is prefilled from `?phone=` and the address from `?email=`, both
 * still editable. Neither is a capability: `VerificationCode` carries no user and
 * no tenant, so a code proves control of one address or one number and nothing
 * more (see the contract's note on `verifyPhoneRequestSchema`).
 *
 * SAME SINGLE-ERROR CONSTRAINT AS `/verify-email`. `VERIFICATION_CODE_INVALID`
 * covers wrong, expired, spent, attempt-capped and unknown alike, on purpose, so
 * the copy states what is true of all of them and draws no distinction the API
 * refuses to draw.
 *
 * Its states: idle, submitting, error, and done — the "your account is ready"
 * card, since this is the last channel in the deployments that use it.
 */
export function VerifyPhonePage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(false);

  /** One message for any invalid field — the only translated copy we have. */
  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');

  const resolver = useMemo<Resolver<VerifyPhoneRequest>>(() => {
    const validate = zodResolver(verifyPhoneRequestSchema, {
      path: [],
      async: true,
      errorMap: () => ({ message: validationMessage }),
    });
    return (values, context, options) => validate(normalizeValues(values), context, options);
  }, [validationMessage]);

  const form = useForm<VerifyPhoneRequest>({
    resolver,
    defaultValues: {
      email: searchParams.get(VERIFY_EMAIL_PARAM) ?? '',
      phone: searchParams.get(VERIFY_PHONE_PARAM) ?? '',
      code: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values: VerifyPhoneRequest): Promise<void> => {
    setErrorCode(null);
    const request = normalizeValues(values);
    try {
      const result = await api.auth.verifyPhone(request);
      if (result.activated) {
        setIsActivated(true);
        return;
      }
      if (result.remainingChannels.includes('EMAIL')) {
        // Somebody who redeemed the SMS code first. The email code is still
        // outstanding, so send them to the screen that redeems it.
        await navigate(verifyEmailPath(request.email));
        return;
      }
      setErrorCode(UNEXPECTED_FAILURE);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        {isActivated ? (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('verifyDone.title')}
              </h1>
            </CardHeader>
            <CardContent>
              <Alert variant="success">
                <AlertDescription>{t('verifyDone.description')}</AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full">
                <Link to={centralLoginPath(ADMIN_CLIENT)}>{t('verifyDone.signIn')}</Link>
              </Button>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('verifyPhone.title')}
              </h1>
              <CardDescription>{t('verifyPhone.description')}</CardDescription>
            </CardHeader>

            <Form {...form}>
              <form
                noValidate
                onSubmit={(event) => {
                  void onSubmit(event);
                }}
              >
                <CardContent className="flex flex-col gap-4">
                  {errorCode === null ? null : (
                    <p role="alert" className="text-danger text-sm text-pretty">
                      {errorMessage(t, errorCode)}
                    </p>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('verifyPhone.emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('verifyPhone.phoneLabel')}</FormLabel>
                        <FormControl>
                          {/* The platform's one phone control (`@instanexus/ui`,
                              S26-TASK-007) — never a second country list. */}
                          <PhoneInput
                            name={field.name}
                            ref={field.ref}
                            onBlur={field.onBlur}
                            value={field.value}
                            onChange={field.onChange}
                            width="sm"
                            label={t('verifyPhone.phoneLabel')}
                            countryLabel={t('verifyPhone.phoneCountryLabel')}
                            searchLabel={t('verifyPhone.phoneSearchLabel')}
                            searchPlaceholder={t('verifyPhone.phoneSearchPlaceholder')}
                            emptyMessage={t('verifyPhone.phoneEmptyMessage')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('verifyPhone.codeLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>

                <CardFooter className="flex flex-col items-stretch gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? t('verifyPhone.submitting') : t('verifyPhone.submit')}
                  </Button>
                  {/* S26-TASK-021. Reads the LIVE fields rather than the query
                      parameters, so a corrected number is the one that gets the
                      new text — one of the recovery paths the per-target ceiling
                      in `resend-service.ts` is sized for. */}
                  <ResendCodeButton
                    channel="SMS"
                    email={form.watch('email')}
                    phone={form.watch('phone')}
                  />
                  <Button asChild variant="link" className="w-full">
                    <Link to={centralLoginPath(ADMIN_CLIENT)}>{t('verifyDone.signIn')}</Link>
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </>
        )}
      </Card>
    </AuthCardLayout>
  );
}
